import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ProductionSidebar from "@/components/ProductionSidebar";
import { useSidebar } from "@/contexts/SidebarContext";
import { PageTransition } from "@/components/PageTransition";
import { defaultEventDate, eventDatesBetween, festivalDayIso } from "@produce/shared";
import {
  assignmentsForDayQuery,
  briefingQuery,
  crewMembersForDayQuery,
  crewMembersQuery,
  lineupQuery,
} from "@/lib/queries/productionQueries";
import { ReadOnlyScheduleTimeline } from "@/components/ReadOnlyScheduleTimeline";
import { ScheduleExportModal } from "@/components/schedule/ScheduleExportModal";
import { Button } from "@/components/ui/button";
import { FileDown, Share2 } from "lucide-react";
import { EditProductionFAB } from "@/components/EditProductionFAB";
import { stageOptionsByDay } from "@/lib/stageExportOptions";
import ShareScheduleDialog from "@/components/ShareScheduleDialog";
import ScheduleFeedbackPanel from "@/components/ScheduleFeedbackPanel";
import { useScheduleFeedback } from "@/hooks/useScheduleFeedback";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";

const Schedule = () => {
  const { productionId } = useParams();
  const { expanded } = useSidebar();
  // Handed to the feedback panel so its anchor times follow the producer's
  // 12h/24h setting, like the timeline beside it.
  const formatTime = useProducerTimeFormatter();
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const [focusedFeedbackId, setFocusedFeedbackId] = useState<string | null>(null);

  // Client feedback → red "!" markers on the schedule for the selected day.
  const { openItems, resolve, remove } = useScheduleFeedback(productionId);
  const feedbackMarkers = useMemo(
    () =>
      (openItems ?? [])
        .filter((f) => f.event_date === selectedDay && f.stage_name && f.anchor_time)
        .map((f) => ({
          id: f.id,
          stage_name: f.stage_name,
          anchor_time: f.anchor_time,
          author_name: f.author_name,
          body: f.body,
        })),
    [openItems, selectedDay],
  );

  // Fetch all crew members for export (across all days)
  const { data: allCrewMembers } = useQuery({
    ...crewMembersQuery(productionId),
    enabled: !!productionId,
  });

  // Fetch briefing data
  const { data: briefing, isLoading } = useQuery({
    ...briefingQuery(productionId),
    enabled: !!productionId,
  });

  // Fetch lineup
  const { data: lineup } = useQuery({
    ...lineupQuery(productionId),
    enabled: !!productionId,
  });

  // Fetch crew assignments
  const { data: assignments } = useQuery({
    ...assignmentsForDayQuery(productionId, selectedDay),
    enabled: !!productionId && !!selectedDay,
  });

  // Fetch crew members
  const { data: crewMembers } = useQuery({
    ...crewMembersForDayQuery(productionId, selectedDay),
    enabled: !!productionId && !!selectedDay,
  });

  // Calculate event days
  const eventDays = eventDatesBetween(briefing?.festival_date_start, briefing?.festival_date_end);

  // Stages per day, for the export's "which stages" filter.
  const stagesByDay = stageOptionsByDay(lineup ?? [], eventDays);

  // Set initial selected day
  useEffect(() => {
    if (eventDays.length > 0 && !selectedDay) {
      // Festival day, not calendar day — see festivalDayIso. No briefing
      // timezone at hand on this page; viewer-local is the pre-existing
      // behaviour minus the midnight flip.
      setSelectedDay(defaultEventDate(eventDays, festivalDayIso()));
    }
  }, [eventDays, selectedDay]);

  return (
    <div className="flex min-h-screen bg-background">
      <ProductionSidebar productionId={productionId!} />

      <PageTransition
        className="flex flex-1 flex-col overflow-x-hidden bg-background transition-all duration-300"
        style={{ marginLeft: expanded ? "220px" : "72px" }}
      >
        {/* Header - matches Analytics page styling */}
        <div className="border-b border-border bg-background px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold">Schedule</h1>
              <p className="text-sm text-muted-foreground">
                View the full lineup and crew timeline for this production.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShareModalOpen(true)}
                disabled={!briefing}
              >
                <Share2 className="mr-2 h-4 w-4" />
                Share with client
              </Button>
              <Button
                variant="outline"
                onClick={() => setExportModalOpen(true)}
                disabled={!briefing || !lineup || lineup.length === 0}
              >
                <FileDown className="mr-2 h-4 w-4" />
                Export PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Share with client dialog */}
        {briefing && (
          <ShareScheduleDialog
            open={shareModalOpen}
            onOpenChange={setShareModalOpen}
            productionId={productionId!}
          />
        )}

        {/* Export Modal */}
        {briefing && (
          <ScheduleExportModal
            open={exportModalOpen}
            onOpenChange={setExportModalOpen}
            briefingId={productionId!}
            festivalName={briefing.festival_name || "Schedule"}
            eventDays={eventDays}
            crewMembers={allCrewMembers || []}
            stagesByDay={stagesByDay}
            currentDay={selectedDay}
          />
        )}

        {/* Timeline Content */}
        <div className="flex-1 overflow-x-hidden bg-background p-6">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="animate-pulse text-muted-foreground">Loading schedule...</div>
            </div>
          ) : briefing && lineup && assignments && crewMembers ? (
            <ReadOnlyScheduleTimeline
              briefing={briefing}
              lineup={lineup}
              assignments={assignments}
              crewMembers={crewMembers}
              eventDays={eventDays}
              selectedDay={selectedDay}
              onDayChange={setSelectedDay}
              feedbackMarkers={feedbackMarkers}
              onResolveFeedback={resolve}
              onDeleteFeedback={remove}
              focusedFeedbackId={focusedFeedbackId}
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-muted-foreground">No schedule data available</p>
            </div>
          )}

          {/* Client feedback collected via shared review links. Clicking an
              anchored item jumps to and highlights its marker on the schedule. */}
          {productionId && (
            <ScheduleFeedbackPanel
              formatTime={formatTime}
              productionId={productionId}
              onFocusItem={(item) => {
                if (item.event_date) setSelectedDay(item.event_date);
                setFocusedFeedbackId(item.id);
              }}
            />
          )}
        </div>
      </PageTransition>
      {productionId && <EditProductionFAB briefingId={productionId} />}
    </div>
  );
};

export default Schedule;
